import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { toast } from "sonner";

// Admin › Blueprint › Course offer: the video course offer shown right after the ebook payment. (3 Nov 2026)

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const Card = ({ children, className = "" }) => <div className={`bg-white border border-[#e8e1d4] p-4 ${className}`}>{children}</div>;
function Stat({ label, value, sub }) {
    return <Card><p className="text-[11px] text-neutral-500">{label}</p><p className="text-2xl font-display font-semibold">{value}</p>{sub && <p className="text-[11px] text-neutral-400">{sub}</p>}</Card>;
}
const Field = ({ label, children }) => <label className="block text-xs text-neutral-600">{label}<div className="mt-1">{children}</div></label>;
const inputCls = "w-full border border-[#d9cfbc] px-3 py-2 text-sm";

function BumpAdmin() {
    const [b, setB] = useState(null);
    const [cfg, setCfg] = useState(null);
    const load = useCallback(() => api.get("/admin/blueprint/bump").then((r) => { setB(r.data); setCfg((c) => c || r.data.config); }).catch((e) => toast.error(apiError(e.response?.data?.detail))), []);
    useEffect(() => { load(); }, [load]);
    const save = async (patch = {}) => {
        try { const r = await api.put("/admin/blueprint/bump", { ...cfg, ...patch, price: Number({ ...cfg, ...patch }.price) }); setCfg(r.data.config); toast.success("Saved"); load(); }
        catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    };
    if (!b || !cfg) return null;
    return (
        <Card className={cfg.enabled ? "!border-green-300" : "!border-amber-300"}>
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <p className="font-display font-semibold">Release Kit add-on at ebook checkout: {cfg.enabled ? <span className="text-green-700">ON</span> : <span className="text-amber-700">OFF</span>}</p>
                    <p className="text-xs text-neutral-500 mt-1">One tick box in the ebook checkout. Buyers who tick it get {b.files.length} PDFs on their reading page and in the email. Fully automatic.</p>
                </div>
                <button onClick={() => save({ enabled: !cfg.enabled })} className={`${cfg.enabled ? "btn-outline" : "btn-dark"} !py-2.5 !px-5 text-xs`} data-testid="bump-toggle">{cfg.enabled ? "Turn OFF" : "Turn ON"}</button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                <Stat label="Paid ebook orders" value={b.stats.orders} />
                <Stat label="Added the kit" value={b.stats.took} />
                <Stat label="Take rate" value={`${b.stats.take_rate}%`} />
                <Stat label="Kit revenue" value={inr(b.stats.revenue)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <Field label="Price (₹)"><input type="number" value={cfg.price} onChange={(e) => setCfg({ ...cfg, price: e.target.value })} className={inputCls} /></Field>
                <Field label="Tick-box text"><input value={cfg.label} onChange={(e) => setCfg({ ...cfg, label: e.target.value })} className={inputCls} /></Field>
                <div className="sm:col-span-2"><Field label="Short description"><textarea rows={2} value={cfg.sub} onChange={(e) => setCfg({ ...cfg, sub: e.target.value })} className={inputCls} /></Field></div>
            </div>
            <button onClick={() => save()} className="btn-dark !py-2.5 !px-5 text-xs mt-4">Save add-on</button>
        </Card>
    );
}

export default function CourseOfferAdmin() {
    const [d, setD] = useState(null);
    const [cfg, setCfg] = useState(null);
    const [days, setDays] = useState(0);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        api.get("/admin/blueprint/upsell", { params: { days } }).then((r) => {
            setD(r.data);
            setCfg((c) => c || { ...r.data.config, bullets: (r.data.config.bullets || []).join("\n") });
        }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    }, [days]);
    useEffect(() => { load(); }, [load]);

    const save = async (patch = {}) => {
        setSaving(true);
        try {
            const body = { ...cfg, ...patch, bullets: String(({ ...cfg, ...patch }).bullets || "").split("\n").map((s) => s.trim()).filter(Boolean) };
            const r = await api.put("/admin/blueprint/upsell", body);
            setCfg({ ...r.data.config, bullets: r.data.config.bullets.join("\n") });
            toast.success(patch.enabled === undefined ? "Saved" : (patch.enabled ? "Course offer is ON" : "Course offer is OFF"));
            load();
        } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
        setSaving(false);
    };

    if (!d || !cfg) return null;
    const s = d.stats;
    return (
        <div className="space-y-5" data-testid="admin-course-offer">
            <BumpAdmin />
            <Card className={cfg.enabled ? "!border-green-300" : "!border-amber-300"}>
                <div className="flex flex-wrap items-center gap-4 justify-between">
                    <div>
                        <p className="font-display font-semibold">Video course offer after the ebook payment: {d.live ? <span className="text-green-700">ON</span> : <span className="text-amber-700">OFF</span>}</p>
                        <p className="text-xs text-neutral-500 mt-1">When ON, every ebook buyer sees the course page before the ebook. When OFF, buyers go straight to the ebook (as now).
                            {!d.course_found && <b className="text-red-600"> Course "{cfg.course_slug}" not found: add it in Courses first.</b>}</p>
                    </div>
                    <button onClick={() => save({ enabled: !cfg.enabled })} disabled={saving || (!cfg.enabled && !d.course_found)}
                        className={`${cfg.enabled ? "btn-outline" : "btn-dark"} !py-2.5 !px-5 text-xs`} data-testid="offer-toggle">{cfg.enabled ? "Turn OFF" : "Turn ON"}</button>
                </div>
            </Card>

            <div className="flex items-center gap-2 text-xs">
                {[[0, "All time"], [7, "7 days"], [30, "30 days"]].map(([v, l]) => <button key={v} onClick={() => setDays(v)} className={`px-3 py-1.5 border ${days === v ? "bg-espresso text-white border-espresso" : "border-[#d9cfbc]"}`}>{l}</button>)}
                <button onClick={load} className="ml-1 border border-[#d9cfbc] px-2 py-1.5"><RefreshCw className="h-3.5 w-3.5" /></button>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="offer-stats">
                <Stat label="Saw the offer" value={s.view} sub="ebook buyers" />
                <Stat label="Clicked buy" value={s.checkout} />
                <Stat label="Bought the course" value={s.sales} />
                <Stat label="Take rate" value={`${s.take_rate}%`} sub="bought ÷ saw" />
                <Stat label="Course revenue" value={inr(s.revenue)} sub="incl. GST" />
            </div>

            <Card>
                <h4 className="font-display font-semibold text-sm mb-4">Offer page</h4>
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Price (₹)"><input type="number" value={cfg.price} onChange={(e) => setCfg({ ...cfg, price: e.target.value })} className={inputCls} data-testid="offer-price-input" /></Field>
                    <Field label={`Course (${d.course_title || "not found"}) · access: ${d.validity_days ? `${d.validity_days} days` : "lifetime"} (change in Course access)`}><input value={cfg.course_slug} onChange={(e) => setCfg({ ...cfg, course_slug: e.target.value })} className={inputCls} /></Field>
                    <Field label="Small line above the headline"><input value={cfg.kicker} onChange={(e) => setCfg({ ...cfg, kicker: e.target.value })} className={inputCls} /></Field>
                    <Field label="Headline"><input value={cfg.headline} onChange={(e) => setCfg({ ...cfg, headline: e.target.value })} className={inputCls} /></Field>
                    <div className="sm:col-span-2"><Field label="Sub-headline"><textarea rows={2} value={cfg.sub} onChange={(e) => setCfg({ ...cfg, sub: e.target.value })} className={inputCls} /></Field></div>
                    <div className="sm:col-span-2"><Field label="What you'll learn (one per line)"><textarea rows={6} value={cfg.bullets} onChange={(e) => setCfg({ ...cfg, bullets: e.target.value })} className={inputCls} /></Field></div>
                    <Field label="Buy button"><input value={cfg.cta} onChange={(e) => setCfg({ ...cfg, cta: e.target.value })} className={inputCls} /></Field>
                    <Field label="No-thanks link"><input value={cfg.decline} onChange={(e) => setCfg({ ...cfg, decline: e.target.value })} className={inputCls} /></Field>
                </div>
                <label className="mt-4 flex items-start gap-2 text-xs text-neutral-600">
                    <input type="checkbox" checked={!!cfg.capi_purchase} onChange={(e) => setCfg({ ...cfg, capi_purchase: e.target.checked })} className="mt-0.5" />
                    <span>Also send course sales to Meta as a Purchase. Keep this OFF while the ebook ads optimise for Purchase: Meta would count each course sale as another ebook sale.</span>
                </label>
                <div className="mt-4 flex gap-3">
                    <button onClick={() => save()} disabled={saving} className="btn-dark !py-2.5 !px-5 text-xs" data-testid="offer-save">{saving ? "Saving…" : "Save offer"}</button>
                </div>
            </Card>
        </div>
    );
}
